import React from "react";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import styles from "./UploadCsvDialog.module.scss";
import { Box, Dialog, DialogContent, DialogTitle, LinearProgress, Typography } from "@mui/material";
import { chunkArray, excelFileReader, retryWithDelay } from "../../../../libs/helpers";
import { useDispatch } from "react-redux";
import { error } from "../../../../apis/reducers/snackbarsReducer";
import { uploadSagawaCSV, uploadYamatoCSV } from "../../../../apis/invoice-api";
import { CsvUploadResponseData, CsvUploadResponseErrorData } from "../../../../apis/models/ShippingFeeResponseData";
import { Link } from "react-router-dom";
import { AxiosResponse } from "axios";


const UploadCsvDialog: React.FC<UploadCsvDialogProps> = (props: UploadCsvDialogProps) => {
  const dispatch = useDispatch();
  const { open, onClose, onSuccess: onRefresh } = props;
  const [fileName, setFileName] = React.useState<string>("");
  const [errRows, setErrRows] = React.useState<number[]>([]);
  const [isSagawa, setIsSagawa] = React.useState<boolean>(true);
  const enum Step {
    INITIAL = "initial",
    LOADING = "loading",
    RESULT = "result"
  }
  const [step, setStep] = React.useState<"initial" | "loading" | "result">(Step.INITIAL);
  const [progress, setProgress] = React.useState(0);
  const [originalCsv, setOriginalCsv] = React.useState<string[]>([]);
  const [csvUploadResponse, setCsvUploadResponse] = React.useState<CsvUploadResponseData | null>(null);


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) { return }
    const file = e.target.files[0];
    if (!file) { return }
    const rows = await excelFileReader(file) as string[][];
    const fileString = rows.map(item => item.join(","));
    fileString.splice(0,1); // remove header
    setOriginalCsv(fileString);
    setFileName(file.name);
  }

  const handleDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      const file = e.dataTransfer.files[0];
      if (!file) { return }
      if (!file.name.includes(".xls")) {
        dispatch(error("Must be Excel file"));
        return
      }
      const fileStringRows = await excelFileReader(file) as string[][];
      const fileString = fileStringRows.map(item => item.join(","));
      fileString.splice(0,1); // remove header
      setOriginalCsv(fileString);
      setFileName(file.name);
    }
  }

  function splitFileString(fileString: string) {
    const fileRows = fileString.split("\n");
    fileRows.splice(0,1); // remove header
    const newFileRows = fileRows.map(item => item.replaceAll("\r", ""));
    if (newFileRows.length > 0 && newFileRows[newFileRows.length - 1].length === 0) {
      return newFileRows.slice(0, newFileRows.length - 1)
    }
    return newFileRows
  }

  const uploadShippingFee = async () => {
    setStep(Step.LOADING);

    const chunkSize = 100;
    const chunkedCsv = chunkArray(originalCsv, chunkSize)
    const errors: CsvUploadResponseErrorData[] = []
    let errorHeaders = ''

    // iterate through chunks
    // using old style for loop because we need the index, and
    // calling chunkedCsv.entries makes the compiler complain
    // about targeting es2015. So, back to the basics :)
    for (let i = 0; i < chunkedCsv.length; i++) { 
      const chunk = chunkedCsv[i];
      const rowOffset = chunkSize * i; // we need this to offset the error row

      try {
        // upload nth chunk
        let res: AxiosResponse;
        if (isSagawa) {
          res = await retryWithDelay(() => uploadSagawaCSV(chunk));
        } else {
          res = await retryWithDelay(() => uploadYamatoCSV(chunk));
        }

        setCsvUploadResponse((resp) => {
          if (!resp) {
            // if there is no response (first iteration)
            return res.data.data; // return api response directly
          } else {
            // if some data exists already, append response data to it
            resp.errors = [...resp.errors, ...res.data.data.errors];
            resp.successes = [...resp.successes, ...res.data.data.successes];
            return resp;
          }
        });

        // declare error row number array
        const errRowNu: number[] = [];

        // iterate through errors returned from response
        res.data.data.errors.forEach((item: CsvUploadResponseErrorData) => {
          errRowNu.push(item.rowNumber + rowOffset); // push error row number
          errors.push(item); // push whole error (for downloadErrorData)
        });

        // we have some errors, set the error csv headers
        if (errRowNu.length > 0) {
          errorHeaders = isSagawa
            ? "運賃分類,佐川顧客コード,郵便番号,送り状番号,発送先,運賃日付,住所,数量,単価,運賃,請求金額,伝票種別"
            : "店所コード,お客様コード ,分類コード,受付日,原票No.,商品区分1,商品区分2,サイズ,個数,扱店都道府県,扱店市区町村,運賃合計,運賃,立替金,保険料,消費税,軽減税率対象,請求金額";
        }

        // set error rows based on previous value
        setErrRows((errRows) => [...errRows, ...errRowNu]);
      } catch (e) {
        console.log(e);
      }
      setProgress((i /chunkedCsv.length) * 100);
    }
    // download error data
    if (errors.length > 0 && errorHeaders !== '') {
      downloadErrorData(errorHeaders, errors);
    }

    // finished uploading, now set result state
    setStep(Step.RESULT);
    setProgress(0);
    onRefresh();
  }

  function downloadErrorData(header: string, data: CsvUploadResponseErrorData[]) {
    let csvContent = `${header},エラーメッセージ\n`;
    data.forEach(errData => {
      csvContent += `${errData.rowData},${errData.message}\n`;
    })

    // Append unicode BOM
    const blob = new Blob([decodeURIComponent('%ef%bb%bf'), csvContent], {type: 'text/csv;charset=utf-8'})

    let encodedUri = URL.createObjectURL(blob);
    let link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "error_data.csv");
    document.body.appendChild(link);
    link.click();
  }

  const cancel = () => {
    onClose();
    setCsvUploadResponse(null);
    setFileName("");
    setErrRows([]);
    setOriginalCsv([]);
    setStep(Step.INITIAL);
    setIsSagawa(true);
  }

  return (
    <Dialog open={open} onClose={() => cancel()} maxWidth="lg">
      <DialogTitle
        sx={{ fontWeight: 700, fontSize: "24px", padding: "40px 40px 24px" }}
      >
        運賃費アップロード
      </DialogTitle>
      <DialogContent sx={{ padding: "0 40px 24px" }}>
        {
          step === Step.INITIAL ?
          <div>
            <div className={styles.selectDeliveryCompany}>
              <div>
                運送会社<span>*</span>
              </div>
              <RadioGroup row>
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label="佐川急便"
                  sx={{height: "20px"}}
                  checked={isSagawa}
                  onChange={() => setIsSagawa(!isSagawa)}
                />
                <FormControlLabel
                  value="male"
                  control={<Radio />}
                  label="ヤマト"
                  sx={{height: "20px"}}
                  checked={!isSagawa}
                  onChange={() => setIsSagawa(!isSagawa)}
                />
              </RadioGroup>
            </div>
            <div className={styles.fileUpload}>
              <Button variant="contained" component="label">
                ファイルを選択
                <input hidden accept=".xls, .xlsx" type="file" onChange={(e) => handleFileChange(e)}/>
              </Button>
              <div>{fileName}</div>
            </div>
            <div className={styles.drop_box} onDrop={(e) =>  handleDropFile(e)} onDragOver={(e) => e.preventDefault()}>
              <img
                src="/icons/fileUploadLogo.svg"
                alt="fileUploadLogo"
              />
              <p>このエリアにドラッグ＆ドロップでファイルを追加</p>
            </div>
            <div className={styles.functions}>
              <Button variant="outlined" onClick={() => cancel()}>キャンセル</Button>
              <Button variant="contained" onClick={() => uploadShippingFee()} disabled={fileName.length <= 0}>アップロード</Button>
            </div>
          </div> :
          step === Step.LOADING ?
          <div className={styles.loading}>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress variant="determinate" value={progress} />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
                    </Box>
                </Box>
            </Box>
          </div> :
          <div className={styles.result}>
            <div className={styles.result__notice}>運賃費用をアップロードしました。</div>
            <div className={styles.result__table_heading}>下記のデータを更新しました。</div>
            <div className={styles.result__table_wrapper}>
              <table>
                <thead>
                  <tr>
                    <td>ブランド名</td>
                    <td>差額</td>
                    <td>新規登録検数</td>
                    <td></td>
                  </tr>
                </thead>
                <tbody>
                  {
                    csvUploadResponse && csvUploadResponse.successes.map((item, index) => (
                      <tr key={index}>
                        <td>{item.brandName}</td>
                        <td className={item.difference < 0 ? styles.err : ""}>{item.difference}</td>
                        <td>{item.registerCount}</td>
                        <td><Link to={`/invoice/${item.latestInvoiceMasterId}`}>詳細&#62;</Link></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            {
              errRows.length > 0 ?
              <div className={styles.result__error}>
                <span>
                  下記のデータはエラーにより登録されませんでした。<br/>
                  データを確認してください。
                </span>
                <div>行No. {errRows.join(", ")}</div>
              </div> : <></>
            }
            <div className={styles.result__functions}><Button variant="outlined" onClick={() => cancel()}>閉じる</Button></div>
          </div>
        }
      </DialogContent>
    </Dialog>
  );
};

export default UploadCsvDialog;

export type UploadCsvDialogProps = {
  open: boolean,
  onClose: () => void,
  onCancel?: () => void,
  onSuccess: () => void
}
